import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, CalendarDays, X,
  UserCheck, Clock, CheckCircle, AlertCircle, RefreshCw,
  Users, Zap, UserPlus, Sparkles, Briefcase, Download,
} from 'lucide-react';
import { auditsApi, auditAssignmentsApi, usersApi, calendarApi, getApiErrorMessage } from '../api';
import { useAuth } from '../store/AuthContext';
import AssignAuditorsModal from '../components/audits/AssignAuditorsModal';
import Dialog from '../components/common/Dialog';
import FormField, { TextArea } from '../components/common/FormField';
import PageIntro from '../components/ui/PageIntro';
import StatusBadge from '../components/ui/StatusBadge';
import { StatStrip } from '../components/common/WorkspaceSummary';
import { Link, useNavigate } from 'react-router-dom';
import useProtectedClose from '../hooks/useProtectedClose';

/* ────── helpers ────── */
function getStatusMeta() {
  return {
    not_started: { label: uiText("Не начат"),            color: '#344054', bg: '#f2f4f7' },
    in_progress:  { label: uiText("В работе"),            color: '#175cd3', bg: '#eff8ff' },
    ready:        { label: uiText("Готов к закрытию"),    color: '#b54708', bg: '#fffaeb' },
    completed:    { label: uiText("Завершён"),            color: '#027a48', bg: '#ecfdf3' },
    closed:       { label: uiText("Закрыт"),              color: '#027a48', bg: '#ecfdf3' },
    draft:        { label: uiText("Черновик"),            color: '#344054', bg: '#f2f4f7' },
    suspended:    { label: uiText("Приостановлен"),       color: '#b54708', bg: '#fffaeb' },
  };
}

function statusMeta(s) { 
  const STATUS_META = getStatusMeta();
  return STATUS_META[s] || STATUS_META.not_started; 
}

function fmt(d, opts = {}) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleString('ru-RU', { day: '2-digit', month: 'short', ...opts });
}

function toDateParam(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstWeekday(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Mon=0 … Sun=6
}

const MONTHS_RU = [uiText("Январь"), uiText("Февраль"), uiText("Март"), uiText("Апрель"), uiText("Май"), uiText("Июнь"),
  uiText("Июль"), uiText("Август"), uiText("Сентябрь"), uiText("Октябрь"), uiText("Ноябрь"), uiText("Декабрь")];
const DAYS_RU = [uiText("Пн"), uiText("Вт"), uiText("Ср"), uiText("Чт"), uiText("Пт"), uiText("Сб"), uiText("Вс")];

/* ────── Audit chip inside calendar cell ────── */
function AuditChip({ audit, onClick, onDragStart }) {
  const m = statusMeta(audit.status);
  const draggable = !!onDragStart && audit.status !== 'closed' && audit.status !== 'completed';
  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? (e) => {
        e.dataTransfer.setData('text/plain', String(audit.id));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(audit);
        // Полупрозрачный draggable
        e.currentTarget.style.opacity = '0.5';
      } : undefined}
      onDragEnd={draggable ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
      onClick={e => { e.stopPropagation(); onClick(audit); }}
      title={`${audit.title || ''} · ${m.label}${draggable ? uiText(" (перетащите на другую дату для переноса)") : ''}`}
      style={{
        background: m.bg, color: m.color,
        borderRadius: 6, padding: '3px 7px',
        fontSize: 10, fontWeight: 600,
        cursor: draggable ? 'grab' : 'pointer',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: '100%',
        border: `1px solid ${m.color}33`,
        display: 'flex', alignItems: 'center', gap: 5,
        transition: 'transform .12s, box-shadow .12s',
        userSelect: 'none',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateX(2px)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateX(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span style={{
        display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
        background: m.color, flexShrink: 0,
      }}/>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {audit.title || `#${audit.id}`}
      </span>
    </div>
  );
}

/* ────── Audit detail side panel (R12: с встроенным quick-assign) ────── */
export function AuditSidePanel({ audit, assignments, assignLoading, assignError, onAssign, onClose, isAdmin }) {
  const [showAssign, setShowAssign] = useState(false);
  const [auditors, setAuditors] = useState([]);
  const [auditorsLoading, setAuditorsLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [quickAssignBusy, setQuickAssignBusy] = useState(false);
  const busyRef = useRef(false);
  const [quickError, setQuickError] = useState('');
  const canAssign = isAdmin && !['closed', 'completed'].includes(audit.status);
  useEffect(() => {
    if (!canAssign) return undefined;
    let alive = true;
    setAuditorsLoading(true); setAvailabilityError('');
    const date = toDateParam(audit.scheduled_date ? new Date(audit.scheduled_date) : new Date());
    (async () => {
      try {
        const response = await calendarApi.availability(date);
        if (alive) setAuditors(response.data.auditors || []);
      } catch {
        if (alive) setAvailabilityError(uiText("Не удалось проверить занятость. Перед назначением уточните доступность аудитора."));
        try {
          const response = await usersApi.auditors();
          if (alive) setAuditors((response.data || []).map(person => ({ ...person, availabilityUnknown: true })));
        } catch (error) {
          if (alive) { setAuditors([]); setAvailabilityError(getApiErrorMessage(error)); }
        }
      } finally { if (alive) setAuditorsLoading(false); }
    })();
    return () => { alive = false; };
  }, [canAssign, audit.id, audit.scheduled_date]);
  const activeAssignments = assignments.filter(row => row.status !== 'revoked');
  const assignedIds = new Set(activeAssignments.map(row => row.auditor_user_id));
  const quickAssign = async id => {
    if (busyRef.current || assignedIds.has(id)) return;
    busyRef.current = true; setQuickAssignBusy(true); setQuickError('');
    try {
      await auditAssignmentsApi.assign(audit.id, { auditor_user_ids: [id], append_only: true });
      await onAssign?.();
    } catch (error) { setQuickError(getApiErrorMessage(error)); }
    finally { busyRef.current = false; setQuickAssignBusy(false); }
  };
  return <>
    <Dialog label={uiText("Аудит в календаре")} size="sm" onClose={onClose} closeDisabled={quickAssignBusy}>
      <div className="ap-dialog__header">
        <div><span className="ap-badge">{statusMeta(audit.status).label}</span>
          <h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</h2></div>
        <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} disabled={quickAssignBusy} onClick={onClose}><X size={18}/></button>
      </div>
      <div className="ap-dialog__body ap-stack">
        {audit.description && <p className="ap-dialog__description">{audit.description}</p>}
        <div className="ap-panel"><dl className="ap-detail-list">
          {[
            [uiText("Плановая дата"), fmt(audit.scheduled_date, { year: 'numeric', hour: '2-digit', minute: '2-digit' })],
            [uiText("Аудитор"), audit.auditor?.full_name || uiText("Не назначен")],
            [uiText("Проверяемый"), audit.customer?.full_name || audit.meta?.audited_company?.name || uiText("Не указан")],
            [uiText("Шаблон"), audit.template?.name || uiText("Не указан")],
          ].map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>)}
        </dl></div>
        <section className="ap-panel">
          <h3 className="ap-panel__heading"><Users size={17}/>{uiText("Команда аудита")}</h3>
          {assignLoading ? <p role="status">{uiText("Загрузка назначений…")}</p> : assignError ? <div role="alert">{assignError}</div> :
            activeAssignments.length ? activeAssignments.map(row => <div key={row.id} className="ap-calendar-person">
              <div><strong>{row.auditor_name || uiText("Аудитор #{v0}", {v0: (row.auditor_user_id)})}</strong>
                {row.response_note && <p className="ap-dialog__description">{row.response_note}</p>}</div>
              <span className={`ap-badge ${row.status === 'accepted' ? 'ap-badge--success' : 'ap-badge--warning'}`}>
                {{ accepted: uiText("Принято"), pending: uiText("Ожидает ответа"), declined: uiText("Отказ") }[row.status] || row.status}
              </span>
            </div>) : <p className="ap-dialog__description">{uiText("Приглашений в команду пока нет.")}</p>}
        </section>
        {canAssign && <section className="ap-panel">
          <h3 className="ap-panel__heading"><UserPlus size={17}/>{uiText("Добавить аудитора")}</h3>
          <p className="ap-dialog__description">{uiText("Добавление не меняет остальной состав команды и ответы на приглашения.")}</p>
          {availabilityError && <p className="ap-dialog__note" role="status">{availabilityError}</p>}
          {quickError && <div className="ap-dialog__alert" role="alert">{quickError}</div>}
          {auditorsLoading ? <p role="status">{uiText("Проверяем доступность…")}</p> : auditors.length ? auditors.map(person => {
            const assigned = assignedIds.has(person.id);
            return <div key={person.id} className="ap-calendar-person">
              <div><strong>{person.full_name || person.username}</strong>
                <p className="ap-dialog__description">{assigned ? uiText("Уже в составе команды") : person.availabilityUnknown ? uiText("Занятость не проверена") : person.busy ? uiText("Есть другие аудиты в этот день") : uiText("Нет других аудитов в этот день")}</p>
                {!assigned && person.busy && <p className="ap-dialog__description">{person.conflicts?.map(item => item.audit_title).join(', ')}</p>}
              </div>
              <button type="button" className="ap-button ap-button--sm" disabled={assigned || quickAssignBusy || assignLoading || Boolean(assignError)} onClick={() => quickAssign(person.id)}>
                {assigned ? <CheckCircle size={15}/> : <UserPlus size={15}/>} {assigned ? uiText("Назначен") : uiText("Добавить")}
              </button>
            </div>;
          }) : <p className="ap-dialog__description">{uiText("Список аудиторов пуст.")}</p>}
        </section>}
      </div>
      <div className="ap-dialog__footer">
        {!quickAssignBusy && <Link to={`/audits/${audit.id}`} className="ap-button"><Briefcase size={16}/>{uiText("Открыть аудит")}</Link>}
        {canAssign && <button type="button" className="ap-button ap-button--primary" disabled={quickAssignBusy || assignLoading || Boolean(assignError)} onClick={() => setShowAssign(true)}><UserCheck size={16}/>{uiText("Расширенный режим")}</button>}
      </div>
    </Dialog>
    {showAssign && <AssignAuditorsModal audit={audit} onClose={() => setShowAssign(false)} onAssigned={async () => { await onAssign(); setShowAssign(false); }}/>}
  </>;
}

/* ────── Calendar cell ────── */
function dayAuditsFor(audits, date) {
  return audits.filter(a => {
    if (!a.scheduled_date) return false;
    return isSameDay(new Date(a.scheduled_date), date);
  });
}

function auditParticipants(audit) {
  const meta = audit.meta || {};
  const names = [
    audit.auditor?.full_name || audit.auditor?.username,
    audit.customer?.full_name || audit.customer?.username,
    meta.audited_company?.name,
    meta.customer_company?.name,
  ].filter(Boolean);
  return Array.from(new Set(names));
}

export function DaySidePanel({ date, audits, canCreateAudit, onCreate, onAuditClick, onClose }) {
  const title = date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
  return <Dialog label={uiText("Аудиты на выбранный день")} size="sm" onClose={onClose}>
    <div className="ap-dialog__header">
      <div><h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}><CalendarDays size={20}/>{title}</h2>
        <p className="ap-dialog__description">{uiText("Запланировано аудитов: ")}{audits.length}</p></div>
      <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} onClick={onClose}><X size={18}/></button>
    </div>
    <div className="ap-dialog__body ap-stack">
      {audits.length ? audits.map(audit => <button className="ap-calendar-day-item" key={audit.id} type="button" onClick={() => onAuditClick(audit)}>
        <strong>{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</strong>
        <span>{fmt(audit.scheduled_date, { hour: '2-digit', minute: '2-digit' })}</span>
        <span><span className="ap-badge">{statusMeta(audit.status).label}</span></span>
        <span>{auditParticipants(audit).join(' · ') || uiText("Участники ещё не указаны")}</span>
      </button>) : <div className="ap-panel"><strong>{uiText("На этот день ничего не запланировано")}</strong>
        {canCreateAudit && <p className="ap-dialog__description">{uiText("Можно создать аудит с уже заполненной плановой датой.")}</p>}</div>}
    </div>
    <div className="ap-dialog__footer">
      <button type="button" className="ap-button" onClick={onClose}>{uiText("Закрыть")}</button>
      {canCreateAudit && <button type="button" className="ap-button ap-button--primary" onClick={() => onCreate(date)}><Briefcase size={16}/>{uiText("Создать аудит")}</button>}
    </div>
  </Dialog>;
}

function CalendarCell({ date, audits, today, isCurrentMonth, onAuditClick, onDragStartAudit, onDropAudit, onCreateAudit }) {
  const isToday = isCurrentMonth && isSameDay(date, today);
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const dayAudits = dayAuditsFor(audits, date);
  const [isDragOver, setIsDragOver] = useState(false);

  let bg = '#ffffff';
  if (isToday) bg = 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)';
  else if (!isCurrentMonth) bg = '#fafafa';
  else if (isWeekend) bg = '#f8fafc';
  if (isDragOver && isCurrentMonth) bg = 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)';

  return (
    <div className={`audit-calendar-cell ${isToday ? 'is-today' : ''} ${isWeekend ? 'is-weekend' : ''} ${!isCurrentMonth ? 'is-outside-month' : ''}`}
      data-date={toDateParam(date)}
      onDragOver={(e) => {
        if (!isCurrentMonth) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!isDragOver) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const auditId = Number(e.dataTransfer.getData('text/plain'));
        if (auditId && onDropAudit) onDropAudit(auditId, date);
      }}
      onClick={() => {
        if (isCurrentMonth && onCreateAudit) onCreateAudit(date);
      }}
      style={{
        minHeight: 96, padding: '8px 8px 6px',
        background: bg,
        border: `1px solid ${isToday ? '#93c5fd' : isDragOver ? '#7c3aed' : '#f1f5f9'}`,
        borderRadius: 8, overflow: 'hidden',
        transition: 'all 0.15s',
        cursor: isCurrentMonth && onCreateAudit ? 'pointer' : (dayAudits.length > 0 ? 'pointer' : 'default'),
      }}
      onMouseEnter={e => {
        if (isCurrentMonth && !isToday && !isDragOver) e.currentTarget.style.borderColor = '#cbd5e1';
      }}
      onMouseLeave={e => {
        if (isCurrentMonth && !isToday && !isDragOver) e.currentTarget.style.borderColor = '#f1f5f9';
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <div className="audit-calendar-day-number">
          {date.getDate()}
        </div>
        {dayAudits.length > 0 && (
          <div className="audit-calendar-day-count">
            {dayAudits.length}
          </div>
        )}
        {dayAudits.length === 0 && isCurrentMonth && onCreateAudit && (
          <div className="audit-calendar-day-add" aria-hidden="true">
            +
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dayAudits.slice(0, 3).map(a => (
          <AuditChip key={a.id} audit={a} onClick={onAuditClick} onDragStart={onDragStartAudit} />
        ))}
        {dayAudits.length > 3 && (
          <div style={{
            fontSize: 10, color: '#7c3aed', paddingLeft: 4, fontWeight: 600,
            cursor: 'pointer',
          }}>
            +{dayAudits.length - 3}{uiText(" ещё")}</div>
        )}
      </div>
    </div>
  );
}

/* ────── Main page ────── */

/* ────── Reschedule confirm modal (R13) ────── */
export function RescheduleConfirmModal({ audit, newDate, onClose: finishClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const onClose = useProtectedClose(Boolean(reason), finishClose, busy);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const submit = async event => {
    event.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try { await onConfirm(reason); }
    catch (error) { setError(getApiErrorMessage(error)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  return <Dialog label={uiText("Перенос аудита")} size="sm" onClose={onClose} closeDisabled={busy}>
    <div className="ap-dialog__header">
      <div><h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}><CalendarDays size={20}/>{uiText("Перенос аудита")}</h2>
        <p className="ap-dialog__description">{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</p></div>
      <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} disabled={busy} onClick={onClose}><X size={18}/></button>
    </div>
    <form onSubmit={submit}>
      <div className="ap-dialog__body ap-stack">
        <div className="ap-panel"><dl className="ap-detail-list">
          <dt>{uiText("Было")}</dt><dd>{fmt(audit.scheduled_date, { year: 'numeric' })}</dd>
          <dt>{uiText("Станет")}</dt><dd>{newDate.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' })}</dd>
        </dl></div>
        <FormField label={uiText("Причина переноса")} hint={uiText("Необязательно. Причина сохранится в истории аудита.")}>
          <TextArea rows={3} disabled={busy} value={reason} onChange={event => setReason(event.target.value)} placeholder={uiText("Например: перенос по согласованию с проверяемой организацией")}/>
        </FormField>
        {error && <div className="ap-dialog__alert" role="alert">{error}</div>}
      </div>
      <div className="ap-dialog__footer">
        <button type="button" className="ap-button" onClick={onClose} disabled={busy}>{uiText("Отмена")}</button>
        <button type="submit" className="ap-button ap-button--primary" disabled={busy}>{busy ? uiText("Переносим…") : uiText("Подтвердить перенос")}</button>
      </div>
    </form>
  </Dialog>;
}

export default function AuditCalendarPage() {
  const [showMonthGrid, setShowMonthGrid] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const STATUS_META = useMemo(() => getStatusMeta(), []);
  const isAdmin = user?.role === 'admin';
  const canCreateAudit = ['admin', 'auditor'].includes(user?.role);

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const assignmentSequence = useRef(0);
  useEffect(() => () => { assignmentSequence.current += 1; }, []);

  // R13: drag-and-drop состояние
  const [draggedAudit, setDraggedAudit] = useState(null);
  const [pendingReschedule, setPendingReschedule] = useState(null);  // {audit, newDate}

  const handleDragStartAudit = useCallback((audit) => {
    setDraggedAudit(audit);
  }, []);

  const handleDropAudit = useCallback((auditId, newDate) => {
    const audit = audits.find(a => a.id === auditId);
    if (!audit) return;
    // Если та же дата — игнор
    const oldDate = audit.scheduled_date ? new Date(audit.scheduled_date) : null;
    if (oldDate && isSameDay(oldDate, newDate)) {
      setDraggedAudit(null);
      return;
    }
    setPendingReschedule({ audit, newDate });
    setDraggedAudit(null);
  }, [audits]);

  const loadAudits = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await auditsApi.list();
      setAudits(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmReschedule = useCallback(async (reason) => {
    if (!pendingReschedule) return;
    try {
      const { audit, newDate } = pendingReschedule;
      // Берём время от старой даты, если было; иначе 10:00
      const old = audit.scheduled_date ? new Date(audit.scheduled_date) : null;
      const newDt = new Date(newDate);
      newDt.setHours(old ? old.getHours() : 10);
      newDt.setMinutes(old ? old.getMinutes() : 0);
      await calendarApi.reschedule({
        audit_id: audit.id,
        // Existing schedule values are local wall-clock timestamps. Converting
        // to UTC here shifted the displayed time again after the next GET.
        new_date: toDateParam(newDt) + 'T' + [newDt.getHours(), newDt.getMinutes(), 0].map(value => String(value).padStart(2, '0')).join(':'),
        reason: reason || null,
      });
      setPendingReschedule(null);
      await loadAudits();
    } catch (e) {
      throw e; // Keep the open form and its reason available for retry.
    }
  }, [pendingReschedule, loadAudits]);

  useEffect(() => { loadAudits(); }, [loadAudits]);

  const exportCalendar = useCallback(async () => {
    setExporting(true);
    setError('');
    try {
      const response = await calendarApi.exportXlsx(year, month + 1);
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_calendar_${year}_${String(month + 1).padStart(2, '0')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setExporting(false);
    }
  }, [year, month]);

  const loadAssignments = useCallback(async (auditId) => {
    const sequence = ++assignmentSequence.current;
    setAssignLoading(true);
    setAssignError('');
    try {
      const res = await auditAssignmentsApi.list(auditId);
      if (sequence === assignmentSequence.current) setAssignments(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      if (sequence === assignmentSequence.current) setAssignError(getApiErrorMessage(error));
    } finally {
      if (sequence === assignmentSequence.current) setAssignLoading(false);
    }
  }, []);

  const handleSelectAudit = (audit) => {
    setSelectedDay(null);
    setSelected(audit);
    setAssignments([]);
    loadAssignments(audit.id);
  };

  const handleClosePanel = () => { assignmentSequence.current += 1; setSelected(null); };

  const handleSelectDay = useCallback((date) => {
    assignmentSequence.current += 1;
    setSelected(null);
    setSelectedDay(date);
  }, []);

  const handleCloseDayPanel = () => setSelectedDay(null);

  const handleCreateAuditFromDate = useCallback((date) => {
    navigate(`/audits/new?scheduled_date=${toDateParam(date)}`);
  }, [navigate]);

  const selectedDayAudits = useMemo(
    () => selectedDay ? dayAuditsFor(audits, selectedDay) : [],
    [audits, selectedDay]
  );

  /* Build calendar grid */
  const { cells } = useMemo(() => {
    const firstWeekday = getFirstWeekday(year, month);
    const daysInMonth = getDaysInMonth(year, month);
    const prevMonthDays = getDaysInMonth(year, month - 1);

    const cells = [];

    // Leading days from prev month
    for (let i = firstWeekday - 1; i >= 0; i--) {
      cells.push({ date: new Date(year, month - 1, prevMonthDays - i), isCurrentMonth: false });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }
    // Trailing days to fill 6 rows
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
    }

    return { cells };
  }, [year, month]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  /* Stats for current month */
  const monthAudits = useMemo(() =>
    audits.filter(a => {
      if (!a.scheduled_date) return false;
      const d = new Date(a.scheduled_date);
      return d.getFullYear() === year && d.getMonth() === month;
    }),
    [audits, year, month]
  );

  return (
    <div className="workspace-page audit-calendar-page">
      <PageIntro
        icon={Sparkles}
        title={uiText("Календарь аудитов")}
        description={uiText("Проверки по датам. Выберите аудит или день.")}
        aside={(
          <div className="ap-inline-actions">
            <button type="button" className="ap-button ap-button--secondary" onClick={exportCalendar} disabled={exporting}>
              <Download size={15} /> {exporting ? uiText("Формируем…") : uiText("Экспорт XLSX")}
            </button>
            <button type="button" className="ap-button ap-button--secondary" onClick={loadAudits}>
              <RefreshCw size={15} />{uiText(" Обновить")}</button>
          </div>
        )}
      />

      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: `1px solid ${'#fecaca'}`, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Month stats */}
      <StatStrip items={[
          { label: uiText("Аудитов в {v0}", {v0: (MONTHS_RU[month].toLowerCase())}), value: monthAudits.length, color: undefined },
          { label: uiText("В работе"),        value: monthAudits.filter(a => a.status === 'in_progress').length,  color: '#175cd3' },
          { label: uiText("Не начаты"),       value: monthAudits.filter(a => a.status === 'not_started').length,  color: '#344054' },
          { label: uiText("Завершены / закрыты"), value: monthAudits.filter(a => ['completed','closed'].includes(a.status)).length, tone: 'success' },
        ]}/>

      {/* Calendar container */}
      <div className="card">
        {/* Month nav */}
        <div className="ap-calendar-nav">
          <button type="button" className="ap-button ap-calendar-nav__arrow" aria-label={uiText("Предыдущий месяц")} title={uiText("Предыдущий месяц")} onClick={prevMonth}><ChevronLeft size={18} /></button>
            <span className="ap-calendar-nav__month" aria-live="polite">
              {MONTHS_RU[month]} {year}
            </span>
            <button type="button" className="ap-button ap-calendar-nav__arrow" aria-label={uiText("Следующий месяц")} title={uiText("Следующий месяц")} onClick={nextMonth}><ChevronRight size={18} /></button>
            <button type="button" className="ap-button ap-calendar-nav__today" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>{uiText("Сегодня")}</button>
        </div>

        <div className="ap-calendar-mode" role="group" aria-label={uiText("Вид календаря")}>
          <button type="button" className={`ap-button ${!showMonthGrid ? 'ap-button--primary' : 'ap-button--secondary'}`} aria-pressed={!showMonthGrid} onClick={()=>setShowMonthGrid(false)}>{uiText("Список")}</button>
          <button type="button" className={`ap-button ${showMonthGrid ? 'ap-button--primary' : 'ap-button--secondary'}`} aria-pressed={showMonthGrid} onClick={()=>setShowMonthGrid(true)}>{uiText("Месяц")}</button>
        </div>
        <div className={`ap-calendar-month ${showMonthGrid ? 'is-expanded' : ''}`}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, padding: '10px 16px 6px' }}>
          {DAYS_RU.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color:'var(--premium-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="loading-center" style={{ padding: 60 }}><div className="spinner" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, padding: '0 16px 16px' }}>
            {cells.map((cell, i) => (
              <CalendarCell
                key={i}
                date={cell.date}
                isCurrentMonth={cell.isCurrentMonth}
                audits={audits}
                today={today}
                onAuditClick={handleSelectAudit}
                onDragStartAudit={isAdmin ? handleDragStartAudit : null}
                onDropAudit={isAdmin ? handleDropAudit : null}
                onCreateAudit={canCreateAudit ? handleSelectDay : null}
              />
            ))}
          </div>
        )}

        </div>
        <div className="ap-agenda" aria-label={uiText("Аудиты выбранного месяца")}>
          {loading ? <div role="status">{uiText("Загружаем аудиты…")}</div> : monthAudits.length ? [...monthAudits].sort((a,b)=>new Date(a.scheduled_date)-new Date(b.scheduled_date)||a.id-b.id).map(audit=><button type="button" className="ap-agenda__item" key={audit.id} onClick={()=>handleSelectAudit(audit)}>
            <span className="ap-agenda__meta"><CalendarDays size={14}/>{fmt(audit.scheduled_date, {weekday:'short'})}<StatusBadge status={audit.status} type="audit"/></span>
            <strong>{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</strong>
            <span className="ap-agenda__meta">{audit.auditor?.full_name || uiText("Аудитор не назначен")}{uiText(" · Открыть")}</span>
          </button>) : <p className="ap-registry-detail">{uiText("В этом месяце аудитов нет. Выберите другой месяц")}{canCreateAudit ? uiText(" или откройте вид «Месяц», чтобы запланировать проверку.") : '.'}</p>}
        </div>
        {/* Legend */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_META).map(([, m]) => (
            <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: m.bg, border: `1px solid ${m.color}44` }} />
              <span style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Side panel */}
      {selectedDay && (
        <DaySidePanel
          date={selectedDay}
          audits={selectedDayAudits}
          canCreateAudit={canCreateAudit}
          onCreate={handleCreateAuditFromDate}
          onAuditClick={handleSelectAudit}
          onClose={handleCloseDayPanel}
        />
      )}

      {selected && (
        <AuditSidePanel
          key={selected.id}
          assignError={assignError}
          audit={selected}
          assignments={assignments}
          assignLoading={assignLoading}
          isAdmin={isAdmin}
          onClose={handleClosePanel}
          onAssign={() => loadAssignments(selected.id)}
        />
      )}

      {/* R13: Confirm-модалка переноса аудита через drag-and-drop */}
      {pendingReschedule && (
        <RescheduleConfirmModal
          audit={pendingReschedule.audit}
          newDate={pendingReschedule.newDate}
          onClose={() => setPendingReschedule(null)}
          onConfirm={confirmReschedule}
        />
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(30px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
